import { useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { adminApiMessage } from '../adminApi';
import { EvalBadge } from '../evals/EvalBits';
import {
  EXPERT_TEXT,
  GUIDE_SECTIONS,
  expertName,
  guideHref,
  isGuideAnchor,
  type GuideAnchor,
} from '../evals/guide';
import { modelsApi, type ModelRoleView, type SpecialistView } from '../models';

// The Evals guide (TOM-139): what the Evals section is for, what its words
// mean, how to fix one bad answer, and how to work on one expert, in the
// owner's words. Every Evals page, and the Neo and Models pages it names,
// links to its part of it ("How this page works", GuideLink). Each anchor is
// in evals/guide.ts, and guide.test.ts checks that every link lands on one
// and that every console link here names a page the admin has.
//
// Every sentence was checked against the code at the time of writing. When a
// page or a rule changes, change its words here too.
//
// The experts and whether owners get them are read live from GET
// /v1/admin/models (SPECIALISTS in cloud/src/core/neo/specialists/registry.ts),
// never written here.
//
// Three blocks once described what the console could not do yet, and each
// now describes what it does: `expert-runs` (Launch and Runs running one
// expert alone, TOM-533), `regrade` (re-grading a run from its page,
// TOM-257) and `case-agent` (a case naming the agent that should get it
// right, TOM-533).

/** The live experts, or why they could not be read. */
export interface ExpertsState {
  experts: SpecialistView[] | null;
  roles: ModelRoleView[];
  error: string | null;
}

function targetClass(id: GuideAnchor, target: string | null): string | undefined {
  return target === id ? 'eval-guide-target' : undefined;
}

/** A term and what it means, with its anchor. */
function Term({
  id,
  term,
  target,
  children,
}: {
  id: GuideAnchor;
  term: string;
  target: string | null;
  children: ReactNode;
}) {
  return (
    <div id={id} className={targetClass(id, target)}>
      <dt>{term}</dt>
      <dd>{children}</dd>
    </div>
  );
}

/** A block with its own anchor: a part of a section, or a step. */
function Part({
  id,
  target,
  as: Tag = 'div',
  children,
}: {
  id: GuideAnchor;
  target: string | null;
  as?: 'div' | 'li';
  children: ReactNode;
}) {
  return (
    <Tag id={id} className={targetClass(id, target)}>
      {children}
    </Tag>
  );
}

/** A link to another part of the guide. */
function See({ to, children }: { to: GuideAnchor; children: ReactNode }) {
  return <Link to={guideHref(to)}>{children}</Link>;
}

/** The experts, from the server's live list, or why it could not be read. */
export function ExpertList({ state }: { state: ExpertsState }) {
  if (state.experts === null) {
    return (
      <p className="admin-muted" data-testid="eval-guide-experts-error">
        {state.error ?? 'Loading the experts…'}
      </p>
    );
  }
  return (
    <ul className="eval-guide-experts" data-testid="eval-guide-experts">
      {state.experts.map((e) => {
        const role = state.roles.find((r) => r.role === e.role);
        const text = EXPERT_TEXT[e.id];
        return (
          <li key={e.id} data-testid={`eval-guide-expert-${e.id}`}>
            <span className="eval-guide-expert-head">
              <span>{expertName(e.id)}</span>
              {e.live ? (
                <EvalBadge tone="succeeded">On for owners</EvalBadge>
              ) : (
                <EvalBadge tone="muted">Off: built, not given to owners</EvalBadge>
              )}
            </span>
            {text !== undefined ? <span>{text.what}</span> : null}
            <span className="admin-muted eval-sub">
              Its model:{' '}
              {role !== undefined ? <strong>{role.label}</strong> : <code>{e.role}</code>} on{' '}
              <Link to="/admin/models">Manage → Models</Link>.
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/** The guide's text. `target` is the anchor a link landed on, softly marked. */
export function EvalGuideBody({
  experts,
  target,
}: {
  experts: ExpertsState;
  target: string | null;
}) {
  return (
    <>
      <nav className="eval-guide-contents" aria-label="On this page">
        <h2>On this page</h2>
        <ol>
          {GUIDE_SECTIONS.map((s) => (
            <li key={s.id}>
              <See to={s.id}>{s.title}</See>
            </li>
          ))}
        </ol>
      </nav>

      {/* ----- What this section is for ----- */}
      <section className="eval-guide-section" aria-labelledby="purpose">
        <h2 id="purpose" className={targetClass('purpose', target)}>
          What this section is for
        </h2>
        <p>
          The Evals pages test Neo and collect its bad answers. Nothing on them changes what owners
          get. A run asks Neo test questions and grades the answers; the cases inbox turns bad
          answers from real chats into new test questions. Runs spend real money on AI calls, inside
          a budget you set. Two other pages do change Neo:
        </p>
        <Part id="change-neo" target={target}>
          <h3>Neo → Claude</h3>
          <p>
            You describe a change in plain words and Neo drafts it as a card. Nothing happens until
            you click <strong>Start</strong>: then a coding agent makes the change and opens a pull
            request. It can change Neo&apos;s instructions, an expert&apos;s rules or notes, the
            test questions or the app itself; only the build workflows are out of its reach. The
            change&apos;s page lists the files to read closely. Once its checks are green,{' '}
            <strong>Merge…</strong> asks for your authenticator code, and merging ships it to
            production.
          </p>
        </Part>
        <Part id="models" target={target}>
          <h3>Manage → Models</h3>
          <p>
            Picks which AI model does each job: the Neo chat default, the Watch, each expert (such
            as <strong>Neo board editor</strong> and <strong>Neo electrical specialist</strong>),
            the <strong>Default eval judges</strong> and more. A model is saved only after a real
            test call through that job passes. An expert left unset uses the model of the chat it is
            helping, and its own model is used only when Neo hands a question over to it (
            <See to="expert-parts">more on that below</See>).
          </p>
        </Part>
        <p>
          Testing one expert on its own is on Launch (<See to="expert-runs">see below</See>).
          Practice rounds that suggest changes to an expert still run only from the command line (
          <See to="experts-command-line">see below</See>).
        </p>
      </section>

      {/* ----- The words ----- */}
      <section className="eval-guide-section" aria-labelledby="words">
        <h2 id="words" className={targetClass('words', target)}>
          The words
        </h2>
        <dl className="eval-guide-terms">
          <Term id="case" term="Case" target={target}>
            <p>
              One test question, and what a good answer must and must not do. Cases are written in
              the code, not kept in the database, so adding or changing one is a code change. They
              come in two sets:
            </p>
            <ul>
              <li>
                <strong>The short set:</strong> questions about a made-up test boat,{' '}
                <em>SV Evaluator</em>. Some are attacks, such as tricks to get round Neo&apos;s
                rules or requests for unsafe advice, that Neo must refuse; the rest are everyday
                questions it should answer well. The judge decides whether Neo refused the attack or
                really helped, and fixed checks must pass too: the right tools used, the words it
                must say said and the ones it must not left out. Some attacks are refused by fixed
                rules before any AI is asked.
              </li>
              <li>
                <strong>The long set:</strong> the whole short set, plus owner questions asked at
                recorded moments of a real boat&apos;s history, which the judge grades against a
                written checklist and the recorded facts, plus Watch situations. In those, a fixed
                check says whether the Watch rightly raised an alert, wrote a note or stayed quiet,
                and the judge grades its wording.
              </li>
            </ul>
            <p>
              An expert&apos;s questions are short-set cases whose names start with its area, such
              as <code>electrical-</code> (<See to="expert-runs">more below</See>).
            </p>
          </Term>

          <Term id="run" term="Run" target={target}>
            <p>
              One pass through a set of cases with one or more models. Each case is asked several
              times (its repeats, three unless you choose otherwise), and it passes when more than
              half of its repeats pass. Each answer is graded by fixed checks and by a judge, an AI
              model from a different company, and must pass both. A Claude answer is graded by a GPT
              model, any other model&apos;s by Claude first; when a third company&apos;s judge is
              listed it grades too, to show how far the judges agree. The judges are the{' '}
              <strong>Default eval judges</strong> on Manage → Models. Every run records what it
              cost and the exact Neo version it tested.
            </p>
            <p>
              <Link to="/admin/evals/runs">Runs</Link> lists every run, newest first, one line per
              model. The range under a score is its 95% interval: where the true score most likely
              lies, given how many cases and repeats the run had.
            </p>
          </Term>

          <Term id="run-page" term="A run's page" target={target}>
            <p>
              What the run was and what it cost, then a card per model with its scores, then every
              case. Open a case to read each repeat&apos;s answer, the tools Neo used and the
              judge&apos;s reasons. <strong>Compare with the baseline</strong>, or{' '}
              <strong>Compare with run</strong> and a run you pick, shows what changed (
              <See to="compare">Compare</See>). <strong>Pin as the … baseline</strong> makes the run
              a model&apos;s baseline, the run later ones are compared with; only a finished run of
              the whole set, graded by a judge from another company, can be pinned. A run still
              going shows its progress and can be cancelled. A run whose parts ran on two different
              versions of the code (an update shipped mid-run) is marked <strong>Mixed code</strong>{' '}
              and never counts.
            </p>
          </Term>

          <Term id="regrade" term="Re-grading" target={target}>
            <p>
              Grading a run&apos;s stored answers again with the judges you choose, without asking
              Neo again, so it costs only the judging. The result is a new run; the run it grades
              stays as it was. Every finished run&apos;s page has a <strong>Re-grade</strong>{' '}
              section below its details. When some answers were never graded (a judge failed, for
              example because its provider ran out of credit), it moves to the top as{' '}
              <strong>Finish grading this run</strong> and says which judge failed and why.
            </p>
            <p>
              <strong>Re-grade this run…</strong> opens the choices the Launch page has for judges:
              the default judges or another one, each judge grading twice, or a judge from the same
              company as the model it grades. Set a budget, read the estimate, then press{' '}
              <strong>Re-grade…</strong> and <strong>Re-grade</strong> to start it. The page moves
              to the new run, which shows its progress and can be cancelled; its{' '}
              <strong>Kind</strong> links back to the run it grades, and that run lists its
              re-grades. The weekly test also re-grades itself once when its judge fails.
            </p>
          </Term>

          <Term id="launch" term="Launching a run" target={target}>
            <p>
              <strong>Launch a run</strong>, on the Overview and on Runs, opens{' '}
              <Link to="/admin/evals/launch">Evals → Launch</Link>. Choose the set, the models, the
              repeats, the split (long set only), the judges, a case filter and a budget, which is
              required. The case filter takes the start of case names: <code>jb-</code> picks every
              case whose name starts that way, and a full name picks that case and any whose name
              starts with it. The page says how many cases the filter picks and shows an estimate of
              the cost. <strong>Launch run…</strong> asks once more before anything is spent, and a
              run stops before it would go over its budget. A daily limit on all eval spending can
              stop one too; the page says when it is off.
            </p>
          </Term>

          <Term id="splits" term="Train, test and demo" target={target}>
            <p>
              The long set&apos;s questions are split in two. <strong>Train</strong> questions are
              the ones you may read and tune Neo against. <strong>Test</strong> questions are held
              back: on a run&apos;s page their questions, answers and checklists stay hidden, and{' '}
              <strong>Reveal repeat N</strong> shows one in full and is logged. They show whether a
              fix really works or Neo only learnt the practice questions. A run that includes them
              asks you to confirm that first.
            </p>
            <p>
              The short set is not split in the console: it is a pass or fail safety check, and
              nothing in it is hidden. That includes each expert&apos;s held-back questions: an
              expert&apos;s questions are split into practice and held-back halves only when it is
              tested alone from the command line. In a console run, read them only to check a
              result, never to tune the expert against them.
            </p>
            <p>
              <strong>Demo</strong> is the METS demo set. It is never hidden and counts in neither
              half. An agent runs it only as a measurement, at the start and end of an improvement
              push, and never tunes against it (ADR 0421); Mike may run it any time. So a console
              run leaves the demo questions out, the demo board questions and the demo owner
              questions alike, whatever the case filter picks, unless you tick{' '}
              <strong>Include the METS demo questions</strong> on the launch page. Ticked, the case
              filter can pick them like any other question. The weekly test always leaves them out.
              A run with them and a run without them both count as the whole set: compared, a demo
              question only one of them holds is listed as in one run only, never as a fall.
            </p>
          </Term>

          <Term id="labels" term="Labels" target={target}>
            <p>
              You grade a sample of stored answers yourself, without seeing the judge&apos;s verdict
              first. Under <strong>Draw a new set</strong>, <strong>Draw set</strong> picks the
              answers; on the set&apos;s page, <strong>Start labelling</strong> shows them one at a
              time, and after you save each one you see what the judge said. Only the labelling
              hides the verdict: run pages show it, so do not look an answer up there before you
              label it.
            </p>
          </Term>

          <Term id="agreement" term="Agreement" target={target}>
            <p>
              A set&apos;s <strong>Agreement report</strong> shows how often the judge agrees with
              you, as a percentage and as a score that allows for agreeing by chance, and every
              answer where it did not. That is how you know the pass rates mean anything. The rule
              for changing judges: change the <strong>Default eval judges</strong> on Manage →
              Models only to a judge that agrees with you at least as well, on a set you have
              labelled all the way through. The console does not check that for you, and measuring a
              new judge against your labels still needs the command line.
            </p>
          </Term>

          <Term id="cases-inbox" term="The cases inbox" target={target}>
            <p>
              <Link to="/admin/evals/cases">Evals → Cases</Link> collects Neo&apos;s bad turns from
              real chats by itself: a thumbs-down, an answer Neo&apos;s output check blocked, a
              safety or security refusal, a message where Jev and the older word-matching check
              disagreed (Neo → Gate), and turns you tagged or saved from a transcript. One turn is
              one candidate, however many of these it has. The list holds no chat text; its{' '}
              <strong>Transcript</strong> link asks for a reason and is logged.
            </p>
          </Term>

          <Term id="case-draft" term="Writing a case" target={target}>
            <p>
              Open a candidate by its title. Write the question in your own words (read the turn,
              never paste it), pick its kind (attack, helpful or owner question) and a category, and
              add <strong>Must</strong> and <strong>Must not</strong> lines, each one something a
              grader can answer yes or no. Say which agent should get it right: Neo, or the expert
              whose own answer was wrong (<See to="case-agent">Which agent should get it right</See>
              ). Choose what it runs on: the made-up boat, or a recorded moment when the boat has a
              consent. A gate disagreement asks only what the gate should have done: allow or
              refuse. <strong>Save and accept</strong> saves and accepts it;{' '}
              <strong>Reject…</strong> and <strong>Mark a duplicate…</strong> are there too.
            </p>
            <p>
              <strong>Export accepted…</strong> on the Cases page downloads every accepted case not
              yet in the tests as one file. A developer adds that file to the tests with a command,
              which checks the consent and copies any recorded moment; Neo → Claude cannot do that
              step. The case reads <strong>In corpus</strong> once a run has asked it.
            </p>
          </Term>

          <Term id="case-agent" term="Which agent should get it right" target={target}>
            <p>
              On a candidate, <strong>Which agent should get this right?</strong> picks{' '}
              <strong>Neo</strong> (the default) or one of the experts: Board editor, Electrical
              (Sparky), Mechanical or Electronics. Pick Neo when Neo asked the wrong expert or none,
              or told the answer wrong. Pick the expert when its own answer, or the board change,
              was wrong. Every case is still asked of Neo with its live experts; an expert&apos;s
              case also becomes one of the questions that expert is tested on alone, and the Cases
              list can show only one agent&apos;s cases (<strong>For</strong>).
            </p>
            <p>
              Choosing an expert fills in its category for you. A category of an expert&apos;s
              (board, electrical, mechanical, electronics, dashboard) is that expert&apos;s alone,
              so a case for Neo takes another. An expert&apos;s case is a question the expert should
              have answered on the made-up boat: not an attack (every attack is already tried on
              each expert), not a recorded moment yet, and one the input gate lets through. The
              choice is not offered for a gate disagreement or a refused message, which no expert
              saw. When the case is added to the tests, a developer finishes what the expert is
              graded on alone, such as whether it should answer fully, in part or say it can&apos;t
              tell.
            </p>
          </Term>

          <Term id="consents" term="Consents" target={target}>
            <p>
              A boat owner&apos;s permission to copy that boat&apos;s real data into the tests as
              recorded moments. It matters when you write a case from the inbox: with a consent the
              case can run on a recorded moment of that boat; without one it runs on the made-up
              boat. Consents are recorded and revoked on{' '}
              <Link to="/admin/evals/cases/consents">Recorded-moment consents</Link>, from Evals →
              Cases. Revoking one lists the recorded moments and cases a developer must then take
              out of the tests. Deleting the owner&apos;s account or the boat revokes it too. For
              now only your own boats can have one.
            </p>
          </Term>

          <Term id="compare" term="Compare" target={target}>
            <p>
              Two runs side by side: run A, the one under review, against run B, the reference, or
              against each model&apos;s pinned baseline. For each model it lists the cases newly
              failing, newly passing, still failing and flaky, the ones it cannot compare (the case
              changed between the runs) and the ones in only one run. Each score shows how much it
              moved and whether run A is higher or lower. <strong>Not distinguishable</strong> means
              the move is too small for these runs to tell from chance; it does not mean the runs
              are the same.
            </p>
          </Term>

          <Term id="overview" term="Overview" target={target}>
            <p>
              <Link to="/admin/evals">Evals → Overview</Link> answers one question: has the Neo that
              owners get been tested? Each model, in the job it does today (Neo chat or Watch) and
              on each set, reads <strong>Evaluated</strong> or <strong>Not evaluated</strong>. Only
              a finished run of the whole set, of that exact Neo version, graded by a judge from
              another company, counts; for the long set it must include the held-back questions. A
              version is a name like v61 plus a code that changes by itself whenever Neo&apos;s
              instructions or tools change, so any such change reads <strong>Not evaluated</strong>{' '}
              until a run of the new version. The page also shows eval spending and how the scores
              trend.
            </p>
          </Term>

          <Term id="weekly" term="The weekly test" target={target}>
            <p>
              The <strong>Weekly scheduled run</strong> panel on the overview. It is off unless you
              turn it on. When on, it runs once a week, at the time the panel shows in your own time
              zone. The time is fixed in UTC, which the panel shows beside it, so where clocks
              change for daylight saving the local time moves by an hour, and the panel says from
              when. A run tests the Neo and Watch models owners get, through the long set on its
              held-back questions (which takes in the whole short set), once each, within the budget
              the panel shows. It sends one email when the short set&apos;s safety bar fails, a
              held-back score falls clearly below the model&apos;s pinned baseline, or the run hit
              errors or could not start or finish. A clean run sends nothing. Without a pinned
              baseline it cannot tell whether scores fell, so pin one from a run&apos;s page.
            </p>
          </Term>
        </dl>
      </section>

      {/* ----- Fixing one bad answer ----- */}
      <section className="eval-guide-section" aria-labelledby="fix-one-answer">
        <h2 id="fix-one-answer" className={targetClass('fix-one-answer', target)}>
          Fixing one bad answer
        </h2>
        <ol className="eval-guide-steps">
          <Part id="fix-find" target={target} as="li">
            <strong>Find it.</strong> <Link to="/admin/neo">Neo → Chats</Link> opens on{' '}
            <strong>Needs attention</strong>; the <strong>Thumbs-down</strong> tab shows only those.{' '}
            <Link to="/admin/neo/refusals">Neo → Refusals</Link> lists what Neo refused, and{' '}
            <Link to="/admin/neo/gate">Neo → Gate</Link> the messages where Jev and the older
            word-matching check disagreed. Each asks for a reason before it shows anyone&apos;s
            words, and every look is logged. From a chat, <strong>Open transcript →</strong>.
          </Part>
          <Part id="fix-mark" target={target} as="li">
            <strong>Mark it.</strong> Under the bad turn, pick a <strong>Tag</strong> (
            <code>hallucination</code>, <code>false-refusal</code>, <code>coverage-gap</code>,{' '}
            <code>ux</code> or <code>bug</code>) or <strong>Save as eval case…</strong>. Either puts
            the turn in Evals → Cases. Thumbs-downs, blocked answers, safety refusals and gate
            disagreements land there by themselves.
          </Part>
          <Part id="fix-write" target={target} as="li">
            <strong>Write the test.</strong> On <Link to="/admin/evals/cases">Evals → Cases</Link>,
            open the candidate and write the case in your own words, with Must and Must not lines (
            <See to="case-draft">Writing a case</See>). <strong>Save and accept</strong>, then{' '}
            <strong>Export accepted…</strong> and give the file to a developer to add.
          </Part>
          <Part id="fix-change" target={target} as="li">
            <strong>Fix Neo.</strong> In <Link to="/admin/neo/changes">Neo → Claude</Link>, describe
            it: &ldquo;When asked Y, Neo says X; it should say Z.&rdquo; Click{' '}
            <strong>Start</strong>, read the pull request, and merge it once its checks are green.
          </Part>
          <Part id="fix-check" target={target} as="li">
            <strong>Check it.</strong> Once the fix has shipped and the case is in the tests, on{' '}
            <Link to="/admin/evals/launch">Evals → Launch</Link> run the case&apos;s set (short for
            the made-up boat, long for a recorded moment) with the case filter set to its case id,
            shown on its draft, and a small budget: it should pass. Then run the whole set and, from
            the run&apos;s page, compare it with the baseline: nothing should be newly failing.
          </Part>
        </ol>
      </section>

      {/* ----- Working on one expert ----- */}
      <section className="eval-guide-section" aria-labelledby="experts">
        <h2 id="experts" className={targetClass('experts', target)}>
          Working on one expert
        </h2>
        <p>
          An expert is a smaller agent behind Neo for one area of the boat. Neo stays the only
          voice: an expert&apos;s work reaches the owner through Neo&apos;s answer.
        </p>

        <Part id="experts-list" target={target}>
          <h3>The experts</h3>
          <ExpertList state={experts} />
          <p>
            The console has no switch to give an expert to owners or take one away: it is a one-line
            code change, which you can ask for in Neo → Claude.
          </p>
        </Part>

        <Part id="expert-parts" target={target}>
          <h3>What an expert is made of</h3>
          <ul>
            <li>
              <strong>Rules:</strong> a few paragraphs on how to think about its area, true of every
              boat.
            </li>
            <li>
              <strong>Knowledge notes:</strong> one piece of method per file, true of every boat,
              each naming the practice questions that showed it was needed.
            </li>
            <li>
              <strong>Tools:</strong> what it may read (and, for the board editor, change), and the
              readings it gets before it starts, such as Sparky&apos;s power report.
            </li>
            <li>
              <strong>Model:</strong> set on Manage → Models, and used when Neo hands a question
              over to the expert. In a board&apos;s Edit chat, and when Jev brings Sparky into
              Neo&apos;s own answer, Neo&apos;s model does the work.
            </li>
          </ul>
        </Part>

        <Part id="teach-expert" target={target}>
          <h3>Teaching an expert something</h3>
          <p>
            A way of working goes in as a rule. A piece of method goes in as a knowledge note. A
            fact about one boat never goes in either: the expert reads it from that boat&apos;s own
            data. The code checks every note: it must name no boat, give a source for any number
            with a unit, and point only at practice questions, never held-back or demo ones. A
            note&apos;s source (a standard, a maker&apos;s manual or a web address) sits in its own
            field: it is there for whoever checks the note, and the expert never reads it. The
            electrical and mechanical experts&apos; rules and notes hold no numbers at all, even
            with a source, and every expert&apos;s text is checked against the held-back owner
            questions so their answers cannot leak in.
          </p>
          <p>
            If the expert would have known but was never asked, the fix is not in its rules. For
            Sparky (and the others once they are on), fix the yes-or-no question Jev asks about each
            message, which has its own test (<code>pnpm neo:eval:route</code>). For the board
            editor, fix Neo&apos;s own instructions about boards.
          </p>
        </Part>

        <Part id="boat-notebook" target={target}>
          <h3>A boat&apos;s notebook</h3>
          <p>
            Each boat keeps a notebook: short notes about that one boat, shared by all its owners. A
            note is something an owner told Neo, or something Neo or an expert noticed in the
            boat&apos;s readings. To read one, open <Link to="/admin/boats">Boats</Link> and choose{' '}
            <strong>Notebook</strong> under the boat&apos;s name. It shows every note, in use or
            not, what each one rests on, and where it came from: &ldquo;an owner&rdquo; or &ldquo;a
            former owner&rdquo;, never a name. A note an owner forgot keeps only its place; its
            words are gone.
          </p>
          <p>
            You give a reason before it opens. The notes hold what owners said about their own boat,
            so every read is logged with your account and your reason, and if the log cannot be
            written the page shows nothing.
          </p>
          <p>
            An eval run&apos;s page shows the notes each answer tried to write. Open a case: under
            each repeat, <strong>Boat notes proposed</strong> lists Neo&apos;s notes and an
            expert&apos;s, with each note&apos;s words, what it cited, and whether the notebook kept
            it, refused it (and why, in plain words) or marked a note contradicted. An eval writes
            them to its own practice notebook, never to a real boat&apos;s. On a held-back question
            they stay hidden with the answer until you reveal it.
          </p>
        </Part>

        <Part id="experts-console" target={target}>
          <h3>What the console can do for an expert</h3>
          <ul>
            <li>
              <Link to="/admin/models">Manage → Models</Link>: change its model.
            </li>
            <li>
              <Link to="/admin/neo/changes">Neo → Claude</Link>: ask for a change to its rules or
              notes, to Jev&apos;s question, or to give it to owners or take it away.
            </li>
            <li>
              <Link to="/admin/evals/launch">Evals → Launch</Link>: run its questions (next).
            </li>
          </ul>
        </Part>

        <Part id="expert-runs" target={target}>
          <h3>Running one expert&apos;s questions</h3>
          <p>
            On Evals → Launch, choose the expert under <strong>Agent</strong>. It then answers its
            own questions alone, without the rest of Neo, as the command line&apos;s{' '}
            <code>--subject</code> runs it. Choose its practice questions (train), its held-back
            questions (test) or both: held-back questions need a second confirmation, and on the
            run&apos;s page their answers stay hidden until you reveal one. The run&apos;s page
            scores the practice and the held-back questions apart (train and test), beside the score
            over all of them. The METS demo questions are never among them. The run is kept under
            the expert, with the expert&apos;s own version, and only a run of all its questions can
            be its baseline.
          </p>
          <p>
            On <Link to="/admin/evals/runs">Runs</Link>, the <strong>Agent</strong> filter shows one
            expert&apos;s runs, and every row, run page and comparison names the agent and its
            version. The Overview lists each live expert and whether its version has a run yet.
          </p>
          <p>
            To ask an expert&apos;s questions of Neo with its live experts instead, choose{' '}
            <strong>Neo (chat) and the Watch</strong> and set the case filter to the start of the
            expert&apos;s question names: <code>electrical-</code> for Sparky, <code>board-</code>{' '}
            for the board editor (a few of its questions start <code>overview-</code> instead). Two
            differences from what owners get: these runs do not use Jev&apos;s spotting yet, so
            Sparky is asked only when Neo chooses to ask him; and <code>board-</code> leaves out the
            METS demo board questions unless you tick{' '}
            <strong>Include the METS demo questions</strong>.
          </p>
        </Part>

        <Part id="experts-command-line" target={target}>
          <h3>What still needs the command line</h3>
          <ul>
            <li>
              Comparing Neo without the expert, with it to ask, with its rules built in, and with
              Jev bringing it in (<code>--specialists</code>, <code>--route</code>).
            </li>
            <li>
              Practice rounds (<code>pnpm neo:train</code>): an AI reads the expert&apos;s failed
              practice questions and proposes one change. A person accepts it onto a branch and
              measures it with a paid run. It is kept only if the practice score clearly rises and
              nothing gets worse: no question that passed now fails, no score falls, and the
              held-back questions were measured and did not drop.
            </li>
          </ul>
        </Part>
      </section>
    </>
  );
}

export function AdminEvalGuide() {
  const location = useLocation();
  const hash = decodeURIComponent(location.hash.slice(1));
  const target = isGuideAnchor(hash) ? hash : null;
  const [experts, setExperts] = useState<ExpertsState>({ experts: null, roles: [], error: null });

  useEffect(() => {
    let ignore = false;
    modelsApi
      .view()
      .then((v) => {
        if (!ignore) setExperts({ experts: v.specialists, roles: v.roles, error: null });
      })
      .catch((err) => {
        if (!ignore) {
          setExperts({
            experts: null,
            roles: [],
            error: adminApiMessage(err, 'The list of experts could not be read.'),
          });
        }
      });
    return () => {
      ignore = true;
    };
  }, []);

  // A link from another page lands on its part of the guide. React Router
  // does not scroll to a hash, so the page does: now, and again once the
  // experts arrive and the page above the anchor may have grown.
  useEffect(() => {
    if (target === null) return;
    document.getElementById(target)?.scrollIntoView({ behavior: 'instant', block: 'start' });
  }, [target, experts.experts]);

  return (
    <section className="admin-section eval-page eval-guide">
      <header className="admin-section-header">
        <h1>How Evals work</h1>
      </header>
      <p className="admin-hint">
        What the Evals pages do, what their words mean, and how to use the console to make Neo and
        its experts better.
      </p>
      <EvalGuideBody experts={experts} target={target} />
    </section>
  );
}
